import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Search, Edit, Trash2, Clock, DollarSign, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ServiceForm } from "@/components/professional/ServiceForm";
import { ProductForm } from "@/components/professional/ProductForm";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

type Service = {
  id: string;
  service_name: string;
  description: string;
  duration: number;
  price: number;
  address: string;
  photo_url: string;
  currency?: string;
  phone?: string;
  email?: string;
  country?: string;
  category?: string;
  keywords?: string[];
};

type Product = {
  id: string;
  title: string;
  description: string;
  price: number;
  currency?: string;
  photo_url: string;
  condition?: string;
  address?: string;
  phone?: string;
};

const MobileProfessionalServices = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<"services" | "products">("services");
  const [deletingServiceId, setDeletingServiceId] = useState<string | null>(null);
  const [deletingProductId, setDeletingProductId] = useState<string | null>(null);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);

  const { data: services, isLoading: isServicesLoading } = useQuery({
    queryKey: ["professional-services"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("professional_services")
        .select("*")
        .eq("professional_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const { data: products, isLoading: isProductsLoading } = useQuery({
    queryKey: ["professional-products"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("professional_products")
        .select("*")
        .eq("professional_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const filteredServices = services?.filter(service => 
    service.service_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    service.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredProducts = products?.filter(product =>
    product.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    product.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDeleteService = async () => {
    if (!deletingServiceId) return;

    try {
      const { error } = await supabase
        .from("professional_services")
        .delete()
        .eq("id", deletingServiceId);

      if (error) throw error;

      toast({
        title: "Service supprimé",
        description: "Le service a été supprimé avec succès",
      });
      
      queryClient.invalidateQueries({ queryKey: ["professional-services"] });
    } catch (error) {
      console.error("Error deleting service:", error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de supprimer le service",
      });
    } finally {
      setDeletingServiceId(null);
    }
  };

  const handleDeleteProduct = async () => {
    if (!deletingProductId) return;

    try {
      const { error } = await supabase
        .from("professional_products")
        .delete()
        .eq("id", deletingProductId);

      if (error) throw error;

      toast({
        title: "Produit supprimé",
        description: "Le produit a été supprimé avec succès",
      });
      
      queryClient.invalidateQueries({ queryKey: ["professional-products"] });
    } catch (error) {
      console.error("Error deleting product:", error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de supprimer le produit",
      });
    } finally {
      setDeletingProductId(null);
    }
  };

  const getCurrencySymbol = (currencyCode: string = 'EUR') => {
    switch (currencyCode) {
      case "EUR": return "€";
      case "USD": return "$";
      case "HTG": return "G";
      case "DOP": return "RD$";
      case "GBP": return "£";
      case "CAD": return "CA$";
      case "CHF": return "Fr";
      case "AUD": return "A$";
      case "JPY": return "¥";
      case "CNY": return "¥";
      case "RUB": return "₽";
      case "BRL": return "R$";
      case "MXN": return "Mex$";
      case "INR": return "₹";
      case "MAD": return "DH";
      case "XAF": return "FCFA";
      case "XOF": return "CFA";
      case "NGN": return "₦";
      case "ZAR": return "R";
      case "KES": return "KSh";
      default: return currencyCode;
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="sticky top-0 z-10 border-b bg-white px-4 py-3 flex items-center shadow-sm">
        <Button 
          variant="ghost" 
          size="icon" 
          className="mr-2" 
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-5 w-5" />
          <span className="sr-only">Retour</span>
        </Button>
        <h1 className="text-lg font-semibold text-gray-800">Services et produits</h1>
      </div>

      {/* Search and Add */}
      <div className="p-4 space-y-4">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher..."
              className="pl-9"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Button onClick={() => navigate("/professional/services")}>
            <Plus className="h-4 w-4 mr-2" />
            <span>Ajouter</span>
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as "services" | "products")}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="services">Services</TabsTrigger>
            <TabsTrigger value="products">Produits</TabsTrigger>
          </TabsList>

          {/* Services Tab */}
          <TabsContent value="services" className="mt-2">
            <ScrollArea className="h-[calc(100vh-200px)]">
              <div className="space-y-3">
                {isServicesLoading ? (
                  Array(3).fill(0).map((_, i) => (
                    <Card key={i} className="animate-pulse">
                      <CardContent className="p-0 h-32" />
                    </Card>
                  ))
                ) : filteredServices?.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    {searchQuery ? "Aucun service trouvé" : "Vous n'avez pas encore créé de services"}
                  </div>
                ) : (
                  filteredServices?.map((service: Service) => (
                    <Card key={service.id}>
                      <CardContent className="p-4">
                        <div className="flex gap-3">
                          <Avatar className="h-12 w-12 rounded-md">
                            <AvatarImage src={service.photo_url} alt={service.service_name} />
                            <AvatarFallback className="rounded-md bg-primary/10 text-primary">
                              {service.service_name.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <div className="flex justify-between">
                              <h3 className="font-medium">{service.service_name}</h3>
                              <div className="flex space-x-1">
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => setEditingService(service)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  className="h-8 w-8 text-red-500"
                                  onClick={() => setDeletingServiceId(service.id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                            <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                              {service.description}
                            </p>
                            <div className="flex gap-3 mt-2">
                              <div className="flex items-center gap-1 text-sm">
                                <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                                <span>{service.duration} min</span>
                              </div>
                              <div className="flex items-center gap-1 text-sm">
                                <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
                                <span>{service.price?.toFixed(2)} {getCurrencySymbol(service.currency)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </ScrollArea>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products" className="mt-2">
            <ScrollArea className="h-[calc(100vh-200px)]">
              <div className="space-y-3">
                {isProductsLoading ? (
                  Array(3).fill(0).map((_, i) => (
                    <Card key={i} className="animate-pulse">
                      <CardContent className="p-0 h-32" />
                    </Card>
                  ))
                ) : filteredProducts?.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    {searchQuery ? "Aucun produit trouvé" : "Vous n'avez pas encore créé de produits"}
                  </div>
                ) : (
                  filteredProducts?.map((product: Product) => (
                    <Card key={product.id}>
                      <CardContent className="p-4">
                        <div className="flex gap-3">
                          <Avatar className="h-12 w-12 rounded-md">
                            <AvatarImage src={product.photo_url} alt={product.title} />
                            <AvatarFallback className="rounded-md bg-green-100 text-green-600">
                              <Package className="h-5 w-5" />
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <div className="flex justify-between">
                              <h3 className="font-medium">{product.title}</h3>
                              <div className="flex space-x-1">
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => setEditingProduct(product)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  className="h-8 w-8 text-red-500"
                                  onClick={() => setDeletingProductId(product.id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                            <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                              {product.description}
                            </p>
                            <div className="flex gap-3 mt-2">
                              <div className="flex items-center gap-1 text-sm">
                                <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
                                <span>{product.price?.toFixed(2)} {getCurrencySymbol(product.currency)}</span>
                              </div>
                              {product.condition && (
                                <div className="flex items-center gap-1 text-sm">
                                  <Package className="h-3.5 w-3.5 text-muted-foreground" />
                                  <span>{product.condition}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </div>

      {/* Service form for editing */}
      <ServiceForm 
        open={!!editingService}
        onOpenChange={() => setEditingService(null)}
        service={editingService || undefined}
      />

      {/* Product form for editing */}
      <ProductForm 
        open={!!editingProduct}
        onOpenChange={() => setEditingProduct(null)}
        product={editingProduct ? {
          id: editingProduct.id,
          title: editingProduct.title,
          description: editingProduct.description,
          price: editingProduct.price,
          condition: editingProduct.condition || "", // Provide a default empty string for condition
          photo_url: editingProduct.photo_url,
          address: editingProduct.address,
          phone: editingProduct.phone,
          currency: editingProduct.currency
        } : undefined}
      />

      {/* Delete service confirmation dialog */}
      <AlertDialog open={!!deletingServiceId} onOpenChange={(open) => !open && setDeletingServiceId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le service</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer ce service ? Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteService}
              className="bg-red-500 hover:bg-red-600"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete product confirmation dialog */}
      <AlertDialog open={!!deletingProductId} onOpenChange={(open) => !open && setDeletingProductId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le produit</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer ce produit ? Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteProduct}
              className="bg-red-500 hover:bg-red-600"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MobileProfessionalServices;
